import React, { useEffect } from 'react';
import { Tree, TreeItem, TreeItemLayout, TreeOpenChangeData } from '@fluentui/react-components';
import { DocumentBulletList20Regular } from '@fluentui/react-icons';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { GuideEmptyState } from '../../../theme/components/GuideEmptyState';
import VisitConceptTreeBranch from '../Molecule/VisitConceptTreeBranch';
import VisitConceptTreeLeaf from '../Atom/VisitConceptTreeLeaf';
import VisitConceptAuthorBadge from '../Atom/VisitConceptAuthorBadge';
import { HCForms } from '../../../models/Entities/Protocols/HCVisit';
import * as Functions from '../VisitConcepts.Functions';
import * as ComponentFunctions from './VisitConceptTree.Functions';
import '../VisitConceptTree.css';

/**
 * Árbol de conceptos de una visita del paciente (respuesta de EMR): visita → [grupos de formularios extra] →
 * formularios → conceptos. Los formularios propios de la visita cuelgan directo de ella; los adicionales,
 * las visitas extra y la finalización anticipada van en su grupo.
 */
const VisitConceptTree = (props: ComponentFunctions.Props) => {
  const { t } = useTranslation();
  const { visit, ContractId } = props;
  const visitValue = `visit-${visit.visitId}`;

  const groups = React.useMemo(() => Functions.groupForms(visit.forms), [visit.forms]);
  const visitCounts = React.useMemo(() => Functions.countVisitConcepts(visit), [visit]);

  // Todo desplegado al abrir (el usuario viene a ver los conceptos); después cada rama se pliega a mano.
  const allBranchValues = React.useMemo(
    () => [
      visitValue,
      ...groups.filter((group) => group.key !== 'visit').map((group) => `group-${visit.visitId}-${group.key}`),
      ...groups.flatMap((group) => group.forms.map((form) => `form-${form.patientFormId}`)),
    ],
    [groups, visit.visitId, visitValue]
  );

  const [openItems, setOpenItems] = React.useState<string[]>(allBranchValues);
  const [active, setActive] = React.useState<string | undefined>(undefined);

  const handleOpenChange = (_event: unknown, data: TreeOpenChangeData) => {
    const items = data.openItems instanceof Set ? Array.from(data.openItems) : Array.isArray(data.openItems) ? data.openItems : [];
    setOpenItems((items as Array<string | number>).map(String));
  };

  // Los nombres de quien completó cada formulario salen del listado de profesionales; se pide una vez si no está.
  useEffect(() => {
    if (!props.practitionersLoaded) props.GetAllPractitioners(1, 1000, '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    props.getAllContractConceptRelationships(ContractId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ContractId, visit.visitId]);

  // Al cambiar de visita el árbol vuelve a abrirse completo.
  useEffect(() => {
    setOpenItems(allBranchValues);
    setActive(undefined);
  }, [allBranchValues]);

  const counter = (counts: Functions.ConceptCounts) => (
    <span
      className={`vct-counter${counts.total > 0 && counts.realized === counts.total ? ' vct-counter--done' : ''}`}
      title={t('visitConcepts.tree.conceptsRealized', { realized: counts.realized, total: counts.total })}
      aria-label={t('visitConcepts.tree.conceptsRealized', { realized: counts.realized, total: counts.total })}
      data-testid="vct-counter"
    >
      {counts.realized}/{counts.total}
    </span>
  );

  const renderForm = (form: HCForms) => {
    const concepts = Functions.formConcepts(form);
    const counts = Functions.countConcepts(form);
    const formValue = `form-${form.patientFormId}`;
    return (
      <VisitConceptTreeBranch
        key={formValue}
        value={formValue}
        kind="form"
        kindLabel={t('visitConcepts.labels.form')}
        title={form.formTitle}
        secondary={form.formType ? form.formType : undefined}
        aside={
          <>
            <VisitConceptAuthorBadge authors={Functions.formAuthors(form, props.practitioners)} />
            {counter(counts)}
          </>
        }
        strong={counts.realized > 0}
        active={active === formValue}
        onActivate={setActive}
        testId={formValue}
      >
        {concepts.length === 0 ? (
          <TreeItem itemType="leaf" value={`${formValue}-empty`}>
            <TreeItemLayout>
              <span className="vct-node__muted">{t('visitConcepts.tree.noConcepts')}</span>
            </TreeItemLayout>
          </TreeItem>
        ) : (
          concepts.map((concept, index) => {
            const value = Functions.conceptKey(form, concept, index);
            return (
              <VisitConceptTreeLeaf
                key={value}
                value={value}
                ContractId={ContractId}
                concept={concept}
                formId={form.formId}
                formName={form.formTitle}
                itemNumber={index}
                active={active === value}
                onActivate={setActive}
              />
            );
          })
        )}
      </VisitConceptTreeBranch>
    );
  };

  const visitSecondary = (
    <>
      {t('visitConcepts.tree.patient', { number: visit.numberInProtocol })} · {t(`visitConcepts.tree.visitType.${Functions.visitTypeKey(visit.visitType)}`)}
    </>
  );

  return (
    <Tree
      aria-label={t('visitConcepts.tree.ariaLabel', { visit: visit.visitTitle })}
      openItems={openItems}
      onOpenChange={handleOpenChange}
      className="vct-tree"
      size="small"
      data-testid="visit-concept-tree"
    >
      <VisitConceptTreeBranch
        value={visitValue}
        kind="visit"
        kindLabel={t('visitConcepts.labels.visit')}
        title={visit.visitTitle}
        secondary={visitSecondary}
        aside={counter(visitCounts)}
        strong
        active={active === visitValue}
        onActivate={setActive}
        testId={visitValue}
      >
        {groups.length === 0 && (
          <TreeItem itemType="leaf" value={`${visitValue}-empty`}>
            <TreeItemLayout>
              <GuideEmptyState icon={<DocumentBulletList20Regular />} title={t('visitConcepts.tree.noForms')} text={t('visitConcepts.tree.noFormsText')} />
            </TreeItemLayout>
          </TreeItem>
        )}
        {groups.map((group) =>
          group.key === 'visit' ? (
            group.forms.map(renderForm)
          ) : (
            <VisitConceptTreeBranch
              key={`group-${visit.visitId}-${group.key}`}
              value={`group-${visit.visitId}-${group.key}`}
              kind="group"
              kindLabel={t('visitConcepts.labels.formGroup')}
              title={t(Functions.formGroupKey(group.key))}
              aside={<span className="vct-counter">{t('visitConcepts.tree.forms', { count: group.forms.length })}</span>}
              active={active === `group-${visit.visitId}-${group.key}`}
              onActivate={setActive}
              testId={`group-${visit.visitId}-${group.key}`}
            >
              {group.forms.map(renderForm)}
            </VisitConceptTreeBranch>
          )
        )}
      </VisitConceptTreeBranch>
    </Tree>
  );
};

export default connect(ComponentFunctions.mapStateToProps, ComponentFunctions.mapDispatchToProps)(VisitConceptTree as any);
